import { useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, ArrowRight } from "lucide-react";
import { Img } from "@/components/Img";
import { useLenis } from "@/components/SmoothScroll";

const Block = ({ title, children }) => (
  <div>
    <h3 className="eyebrow mb-3 text-royal">{title}</h3>
    <div className="text-[0.98rem] leading-relaxed text-navy/85">{children}</div>
  </div>
);

export const ProjectModal = ({ project, next, onClose, onNext }) => {
  const lenis = useLenis();
  useEffect(() => {
    if (!lenis || !project) return undefined;
    lenis.stop();
    return () => lenis.start();
  }, [lenis, project]);

  return (
    <Dialog.Root open={!!project} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-midnight/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        {project && (
          <Dialog.Content
            data-testid="project-modal"
            data-lenis-prevent
            className="fixed inset-x-0 bottom-0 top-6 z-[80] overflow-y-auto rounded-t-2xl bg-paper focus:outline-none data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-10 data-[state=open]:fade-in-0 sm:inset-x-6 sm:top-10 lg:inset-x-16"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-[var(--line)] bg-paper/95 px-5 py-4 backdrop-blur sm:px-10">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-lime px-3 py-1 font-mono text-[0.65rem] uppercase tracking-[0.16em] text-navy" data-testid="project-modal-concept-label">Concept Project</span>
                <span className="rounded-full border border-navy/20 px-3 py-1 font-mono text-[0.65rem] uppercase tracking-[0.16em] text-navy" data-testid="project-modal-category">{project.category}</span>
              </div>
              <Dialog.Close className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-navy text-white transition-colors hover:bg-royal" aria-label="Close project" data-testid="project-modal-close-button">
                <X className="h-5 w-5" aria-hidden="true" />
              </Dialog.Close>
            </div>

            <div className="px-5 pb-16 pt-10 sm:px-10 lg:px-16">
              <Dialog.Title className="font-display text-4xl font-extrabold tracking-[-0.04em] text-navy sm:text-5xl lg:text-7xl" data-testid="project-modal-title">
                {project.title}
              </Dialog.Title>
              <Dialog.Description className="mt-5 max-w-2xl text-base text-slatey sm:text-lg">{project.summary}</Dialog.Description>

              <div className="mt-10 overflow-hidden rounded-lg bg-mist">
                <Img name={project.image} alt={project.alt} eager sizes="90vw" className="max-h-[78vh] w-full object-cover" />
              </div>

              <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-3">
                <Block title="Creative brief">{project.brief}</Block>
                <Block title="Design approach">{project.approach}</Block>
                <Block title="Deliverables">
                  <ul className="space-y-1.5">{project.deliverables.map((d) => <li key={d} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-royal" aria-hidden="true" />{d}</li>)}</ul>
                </Block>
              </div>

              <div className="mt-14 grid gap-4 md:grid-cols-12">
                <div className="on-dark rounded-lg bg-navy p-6 text-white sm:p-10 md:col-span-7">
                  <h3 className="eyebrow text-lime">Colour direction</h3>
                  <div className="mt-6 grid grid-cols-4 gap-2">
                    {project.palette.map((c) => (
                      <div key={c}>
                        <div className="aspect-[3/4] rounded-md ring-1 ring-white/20" style={{ background: c }} />
                        <p className="mt-2 font-mono text-[0.7rem] text-white/75">{c}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg bg-mist p-6 sm:p-10 md:col-span-5">
                  <h3 className="eyebrow text-royal">Typography direction</h3>
                  <p className="mt-4 font-display text-7xl font-extrabold leading-none tracking-[-0.06em] text-navy">Aa</p>
                  <p className="mt-4 text-[0.98rem] leading-relaxed text-navy/85">{project.typography}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="overflow-hidden rounded-lg bg-mist">
                  <Img name={project.image} alt={`${project.title} — detail view`} sizes="45vw" className="aspect-square w-full scale-150 object-cover object-[30%_40%]" />
                </div>
                <div className="overflow-hidden rounded-lg bg-mist">
                  <Img name={project.image} alt={`${project.title} — close-up mockup`} sizes="45vw" className="aspect-square w-full scale-[1.8] object-cover object-[70%_65%]" />
                </div>
              </div>

              <div className="on-dark mt-4 rounded-lg bg-midnight p-6 sm:p-12">
                <h3 className="eyebrow text-lime">Final presentation</h3>
                <div className="mx-auto mt-8 max-w-4xl overflow-hidden rounded-md shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)]">
                  <Img name={project.image} alt={`${project.title} — final presentation`} sizes="80vw" className="w-full object-cover" />
                </div>
                <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-white/75">
                  This is a speculative concept created by PRPO LTD to demonstrate design thinking and craft. It is not commissioned client work.
                </p>
              </div>

              {next && (
                <button
                  type="button"
                  onClick={onNext}
                  data-testid="project-modal-next-button"
                  className="group mt-14 flex w-full items-center justify-between border-t border-[var(--line)] pt-8 text-left"
                >
                  <span>
                    <span className="eyebrow text-slatey">Next concept</span>
                    <span className="mt-2 block font-display text-3xl font-bold tracking-tight text-navy transition-colors group-hover:text-royal sm:text-5xl">{next.title}</span>
                  </span>
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-lime text-navy transition-transform duration-500 ease-expo group-hover:translate-x-2">
                    <ArrowRight className="h-6 w-6" aria-hidden="true" />
                  </span>
                </button>
              )}
            </div>
          </Dialog.Content>
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
};
